import { useMemo } from "react";
import { accountFormat, getCurrencySymbol, getFormatAmount } from "@Common/helpers/number";

// MUI
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import { Box, Tooltip } from "@mui/material";

// Icons
import { ReactComponent as AttentionIcon } from "@/assets/icons/dataPages/inventory/information.svg";

// Components
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";

// Types
import { IDepartmentPositionRecord } from "@/DataPages/types/timeAndPayRates/models";
import { COLUMN_RESIZE_FACTOR, HEADER_ALIGN_RIGHT, HEADER_CLASS_NAME, TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { tableKeys } from "@/Constants/tableKeys";

const ONE_HUNDRED_TEN = 110;
const THIRTY = 30;

const renderSecondaryRatesCell = (secondaryRates: number[]) => {
	const hasSecondaries = secondaryRates.length > 0;
	if (!hasSecondaries) {
		return null;
	}

	const secondaryRatesItems = secondaryRates.map((rate) => {
		const formattedRate = `${getCurrencySymbol()}${getFormatAmount(rate)}`;
		return <span key={rate}>{formattedRate}</span>;
	});

	const tooltipTitle = (
		<div className="flex flex-col">
			<strong>Secondary Rates</strong>
			{secondaryRatesItems}
		</div>
	);

	const tooltipOptions = {
		popper: {
			modifiers: [
				{
					name: "offset",
					options: {
						offset: [30, -10],
					},
				},
			],
		},
	};

	return (
		<Tooltip title={tooltipTitle} placement="bottom" arrow slotProps={tooltipOptions}>
			<Box className="flex justify-center items-center h-full">
				<AttentionIcon className={"fill-gray-text"} />
			</Box>
		</Tooltip>
	);
};

interface IPayRatesTableProps {
	rows: IDepartmentPositionRecord[];
	filterKeySuffix?: string;
}

export default function PayRatesTable(props: Readonly<IPayRatesTableProps>) {
	const { rows } = props;
	const apiRef = useGridApiRef();

	const columns: GridColDef[] = useMemo(
		() => [
			{
				field: "_id",
				headerName: "ID",
				width: 10,
				filterable: false,
			},
			{
				field: "secondaries",
				filterable: false,
				headerName: "",
				cellClassName: "no-padding",
				width: THIRTY,
				maxWidth: THIRTY,
				renderCell(params) {
					return renderSecondaryRatesCell(params.row.secondaries ?? []);
				},
			},
			{
				field: "fullName",
				headerName: "Associate Name",
				minWidth: ONE_HUNDRED_TEN,
				width: ONE_HUNDRED_TEN,
				maxWidth: ONE_HUNDRED_TEN * COLUMN_RESIZE_FACTOR,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => {
					return <CustomCell value={params.value} hasTooltip maxWith={params.colDef.width ?? ONE_HUNDRED_TEN} fontSize="12px" />;
				},
			},
			{
				field: "hours",
				headerName: "Hours Worked",
				minWidth: 110,
				align: "right",
				headerAlign: "right",
				headerClassName: HEADER_CLASS_NAME + HEADER_ALIGN_RIGHT,
				width: 110,
				type: "number",
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => {
					if (!params.value) {
						const value = "0.00";
						return (
							<TypographyCell
								params={params}
								issues={[]}
								hints={{ isFunded: false }}
								fontSize={"12px"}
								render="account"
								formatOptions={{ decimals: 2 }}
							>
								{value}
							</TypographyCell>
						);
					}
					return (
						<TypographyCell
							render="account"
							issues={[]}
							params={params}
							formatOptions={{ decimals: 2 }}
							fontSize={"12px"}
							hints={{ isFunded: false }}
						/>
					);
				},
			},
			{
				field: "rate",
				headerName: "Pay Rates",
				minWidth: 85,
				width: 85,
				align: "right",
				type: "number",
				headerClassName: HEADER_CLASS_NAME + HEADER_ALIGN_RIGHT,
				valueFormatter: (value) => accountFormat(Number(value) || 0),
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: (params) => {
					return (
						<TypographyCell
							render="absolute"
							params={params}
							issues={[]}
							hints={{ isFunded: false, isMoney: true }}
							fontSize={"12px"}
							formatOptions={{ decimals: 2 }}
						/>
					);
				},
			},
		],
		[rows]
	);

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: `${tableKeys.DATA_PAGES.TIME_PAY_RATES_WORK_HOURS}/${props.filterKeySuffix}`,
			}}
			apiRef={apiRef}
			columns={columns}
			rows={rows}
			maxRows={18}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			columnVisibilityModel={{
				_id: false,
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					titleExpandible: {
						className: "[&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
						hide: true,
					},
					filter: {
						hide: false,
					},
					search: {
						useQuickFilter: true,
					},
				},
				noRowsOverlay: {
					title: "No Work Hours or Pay Rates",
				},
			}}
			hideFooter
			customProps={{
				tableContainerStyle: { height: "100%", width: "100%" },
				tableClassName: {
					...TAILWIND_FOR_DATA_SMART_TABLE,
					"[&_.toolbar-actions]:!top-[0px]": true,
					"[&_.MuiDataGrid-main]:mt-[16px]": true,
				},
				expandableRows: true,
				hide: false,
			}}
		/>
	);
}
